import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';

const api = axios.create({ withCredentials: true });

function Analytics() {
  const [overview, setOverview] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);

  useEffect(() => {
    fetchOverview();
    fetchEngagement();
  }, []);

  const fetchOverview = async () => {
    try {
      const response = await api.get(`${API_URL}/analytics/overview`);
      setOverview(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching overview:', error);
      setError('Kunde inte hämta analytics');
      setLoading(false);
    }
  };

  const fetchEngagement = async () => {
    try {
      const response = await api.get(`${API_URL}/analytics/contacts/engagement`);
      setEngagement(response.data.data);
    } catch (error) {
      console.error('Error fetching engagement:', error);
    }
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    try {
      const response = await api.get(`${API_URL}/analytics/campaign/${campaignId}`);
      setCampaignAnalytics(response.data.data);
    } catch (error) {
      console.error('Error fetching campaign analytics:', error);
      setError('Kunde inte hämta kampanj-analytics');
    }
  };

  const handleCampaignClick = (campaign) => {
    if (selectedCampaign?.id === campaign.id) {
      setSelectedCampaign(null);
      setCampaignAnalytics(null);
    } else {
      setSelectedCampaign(campaign);
      fetchCampaignAnalytics(campaign.id);
    }
  };

  if (loading) {
    return (
      <main className="App-main">
        <LoadingSpinner message="Laddar analytics..." />
      </main>
    );
  }

  return (
    <main className="App-main analytics-page">
      <AlertMessage error={error} />

      <h1 className="page-title">Marketing Analytics</h1>

      {/* Overview Cards */}
      {overview && (
        <div className="analytics-overview">
          <div className="analytics-card">
            <div className="analytics-card-icon">👥</div>
            <div className="analytics-card-content">
              <h3>Kontakter</h3>
              <div className="analytics-value">{overview.contacts.total}</div>
              <div className="analytics-subtext">+{overview.contacts.newThisMonth} denna månad</div>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-card-icon">📧</div>
            <div className="analytics-card-content">
              <h3>Mejl skickade</h3>
              <div className="analytics-value">{overview.emails.sent}</div>
              <div className="analytics-subtext">
                {overview.emails.openRate}% öppningsfrekvens
              </div>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-card-icon">📊</div>
            <div className="analytics-card-content">
              <h3>Kampanjer</h3>
              <div className="analytics-value">{overview.campaigns.total}</div>
              <div className="analytics-subtext">{overview.campaigns.active} aktiva</div>
            </div>
          </div>

          <div className="analytics-card">
            <div className="analytics-card-icon">🧲</div>
            <div className="analytics-card-content">
              <h3>Lead Magnets</h3>
              <div className="analytics-value">{overview.leadMagnets.signupsThisMonth}</div>
              <div className="analytics-subtext">signups denna månad</div>
            </div>
          </div>
        </div>
      )}

      {/* Email Performance */}
      {overview && (
        <div className="analytics-section">
          <h2>E-postprestanda</h2>
          <div className="performance-bars">
            <div className="performance-item">
              <div className="performance-label">
                <span>Öppningsfrekvens</span>
                <span className="performance-value">{overview.emails.openRate}%</span>
              </div>
              <div className="performance-bar">
                <div
                  className="performance-fill"
                  style={{ width: `${Math.min(overview.emails.openRate, 100)}%`, backgroundColor: '#10B981' }}
                />
              </div>
            </div>
            <div className="performance-item">
              <div className="performance-label">
                <span>Klickfrekvens</span>
                <span className="performance-value">{overview.emails.clickRate}%</span>
              </div>
              <div className="performance-bar">
                <div
                  className="performance-fill"
                  style={{ width: `${Math.min(overview.emails.clickRate * 5, 100)}%`, backgroundColor: '#3B82F6' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="analytics-grid">
        {/* Top Campaigns */}
        {overview?.topCampaigns?.length > 0 && (
          <div className="analytics-section">
            <h2>Toppkampanjer</h2>
            <div className="top-list">
              {overview.topCampaigns.map(campaign => (
                <div
                  key={campaign.id}
                  className={`top-list-item ${selectedCampaign?.id === campaign.id ? 'selected' : ''}`}
                  onClick={() => handleCampaignClick(campaign)}
                >
                  <div className="top-list-main">
                    <span className="top-list-name">{campaign.name}</span>
                    <span className="top-list-stats">
                      {campaign.totalSent} skickade · {campaign.totalOpened} öppnade
                    </span>
                  </div>
                  <div className="top-list-rate">
                    {campaign.totalSent > 0
                      ? Math.round((campaign.totalOpened / campaign.totalSent) * 100)
                      : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Lead Magnets */}
        {overview?.leadMagnets?.topPerformers?.length > 0 && (
          <div className="analytics-section">
            <h2>Topp Lead Magnets</h2>
            <div className="top-list">
              {overview.leadMagnets.topPerformers.map(lm => (
                <div key={lm.id} className="top-list-item">
                  <div className="top-list-main">
                    <span className="top-list-name">{lm.name}</span>
                  </div>
                  <div className="top-list-value">{lm.downloadCount} nedladdningar</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Campaign Details */}
      {selectedCampaign && campaignAnalytics && (
        <div className="analytics-section campaign-detail-section">
          <h2>Kampanjdetaljer: {campaignAnalytics.campaign.name}</h2>

          <div className="campaign-detail-stats">
            <div className="detail-stat">
              <span className="detail-stat-value">{campaignAnalytics.campaign.totalRecipients}</span>
              <span className="detail-stat-label">Mottagare</span>
            </div>
            <div className="detail-stat">
              <span className="detail-stat-value">{campaignAnalytics.campaign.totalSent}</span>
              <span className="detail-stat-label">Skickade</span>
            </div>
            <div className="detail-stat">
              <span className="detail-stat-value">{campaignAnalytics.campaign.totalOpened}</span>
              <span className="detail-stat-label">Öppnade</span>
            </div>
            <div className="detail-stat">
              <span className="detail-stat-value">{campaignAnalytics.campaign.totalClicked}</span>
              <span className="detail-stat-label">Klickade</span>
            </div>
            <div className="detail-stat highlight">
              <span className="detail-stat-value">{campaignAnalytics.campaign.openRate}%</span>
              <span className="detail-stat-label">Öppningsfrekvens</span>
            </div>
            <div className="detail-stat highlight">
              <span className="detail-stat-value">{campaignAnalytics.campaign.clickRate}%</span>
              <span className="detail-stat-label">Klickfrekvens</span>
            </div>
          </div>

          {/* Step Performance for Drip */}
          {campaignAnalytics.stepPerformance?.length > 0 && (
            <div className="step-performance">
              <h3>Steg-prestanda</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Steg</th>
                    <th>Ämne</th>
                    <th>Skickade</th>
                    <th>Öppnade</th>
                    <th>Klickade</th>
                    <th>Öppn.%</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignAnalytics.stepPerformance.map(step => (
                    <tr key={step.stepOrder}>
                      <td>{step.stepOrder + 1}</td>
                      <td>{step.subject}</td>
                      <td>{step.sent}</td>
                      <td>{step.opened}</td>
                      <td>{step.clicked}</td>
                      <td>{step.openRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            className="btn btn-secondary"
            onClick={() => { setSelectedCampaign(null); setCampaignAnalytics(null); }}
          >
            Stäng detaljer
          </button>
        </div>
      )}

      {/* Engagement Distribution */}
      {engagement && (
        <div className="analytics-section">
          <h2>Kontakt-engagemang</h2>

          <div className="engagement-grid">
            <div className="engagement-distribution">
              <h3>Fördelning</h3>
              <div className="distribution-bars">
                {engagement.distribution.map(item => (
                  <div key={item.engagementLevel} className="distribution-item">
                    <div className="distribution-label">
                      <span>{item.engagementLevel}</span>
                      <span>{item.count}</span>
                    </div>
                    <div className="distribution-bar">
                      <div
                        className="distribution-fill"
                        style={{
                          width: `${(item.count / Math.max(...engagement.distribution.map(d => d.count))) * 100}%`,
                          backgroundColor: item.engagementLevel === 'Hög' ? '#10B981' :
                                         item.engagementLevel === 'Medel' ? '#F59E0B' :
                                         item.engagementLevel === 'Låg' ? '#EF4444' : '#6B7280'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="top-engaged">
              <h3>Mest engagerade kontakter</h3>
              <div className="engaged-list">
                {engagement.topEngaged.map(contact => (
                  <div key={contact.id} className="engaged-item">
                    <div className="engaged-info">
                      <span className="engaged-name">{contact.firstName} {contact.lastName}</span>
                      <span className="engaged-email">{contact.email}</span>
                    </div>
                    <div className="engaged-score">
                      <div className="score-bar">
                        <div
                          className="score-fill"
                          style={{ width: `${contact.leadScore}%` }}
                        />
                      </div>
                      <span className="score-value">{contact.leadScore}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Analytics;
